import React, { useEffect, useRef, useState } from "react";
import { X } from "lucide-react";
import Hls, { ErrorData } from "hls.js";

interface FullscreenVideoModalProps {
  isOpen: boolean;
  videoUrl: string | undefined;
  onClose: () => void;
  authToken?: string | null;
}

const FullscreenVideoModal: React.FC<FullscreenVideoModalProps> = ({
  isOpen,
  videoUrl,
  onClose,
  authToken,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const hlsRef = useRef<Hls | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Setup HLS or native video playback
  useEffect(() => {
    const video = videoRef.current;
    if (!isOpen || !videoUrl || !video) return;

    setIsLoading(true);

    // Check if this is an HLS stream
    const isHlsStream = videoUrl.includes(".m3u8");

    if (isHlsStream && Hls.isSupported()) {
      // Use HLS.js for browsers that don't have native HLS support
      const hls = new Hls({
        enableWorker: true,
        lowLatencyMode: true,
        xhrSetup: (xhr: XMLHttpRequest) => {
          if (authToken) {
            xhr.setRequestHeader("authorization", authToken);
          }
        },
      });
      hlsRef.current = hls;

      hls.loadSource(videoUrl);
      hls.attachMedia(video);

      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        setIsLoading(false);
        video.play().catch((err) => {
          console.log("Autoplay prevented:", err);
        });
      });

      hls.on(Hls.Events.ERROR, (_event: string, data: ErrorData) => {
        console.error("HLS error:", data);
        setIsLoading(false);
      });
    } else if (
      isHlsStream &&
      video.canPlayType("application/vnd.apple.mpegurl")
    ) {
      // Safari has native HLS support
      video.src = videoUrl;
      video.addEventListener("loadedmetadata", () => {
        setIsLoading(false);
        video.play().catch((err) => {
          console.log("Autoplay prevented:", err);
        });
      });
    } else {
      // Regular video file
      video.src = videoUrl;
      video.addEventListener("loadedmetadata", () => {
        setIsLoading(false);
        video.play().catch((err) => {
          console.log("Autoplay prevented:", err);
        });
      });
    }

    return () => {
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
      if (video) {
        video.pause();
        video.src = "";
      }
    };
  }, [isOpen, videoUrl]);

  // Cleanup when modal closes
  useEffect(() => {
    if (!isOpen) {
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
      if (videoRef.current) {
        videoRef.current.pause();
        videoRef.current.src = "";
      }
    }
  }, [isOpen]);

  // Handle Escape key to close
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen || !videoUrl) return null;

  return (
    <div
      className="fixed inset-0 z-50 bg-black flex items-center justify-center"
      onClick={onClose} // Click outside to close
    >
      <div
        className="relative w-full h-full flex items-center justify-center"
        onClick={(e) => e.stopPropagation()} // Prevent closing when clicking video area
      >
        {isLoading && (
          <div className="absolute inset-0 flex items-center justify-center z-20">
            <div className="w-12 h-12 border-4 border-white border-t-transparent rounded-full animate-spin" />
          </div>
        )}

        <video
          ref={videoRef}
          className="w-full h-full object-contain max-h-screen"
          controls
          playsInline
          autoPlay
        />

        {/* Close Button (Top Right) */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 rounded-full bg-black/40 text-white hover:bg-black/60 transition-opacity duration-300 backdrop-blur-md z-10"
        >
          <X className="w-6 h-6" />
        </button>
      </div>
    </div>
  );
};

export default FullscreenVideoModal;
